import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api.js';
import { money } from '../../utils/money.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { ROLES } from '../../config/menuData.js';

const MILESTONE_LABELS = {
  dispatched: 'Dispatched',
  arrived: 'Arrived On-site',
  allfast: 'All Fast',
  commenced: 'Commenced',
  letgo: 'Let Go',
  returning: 'Returning',
  secured: 'Back at Station',
};

function fmt(ts) {
  return ts ? new Date(ts).toLocaleString() : '—';
}

const STATUS_BADGE = { 'In Progress': 'bg-info text-dark', Draft: 'bg-warning text-dark', Approved: 'bg-success' };

export default function InvoicingDraftsPage() {
  // 8956: one consolidated sales invoice per booking; its lines are the jobs.
  const [invoices, setInvoices] = useState([]);
  const [detail, setDetail] = useState(null); // the invoice under review
  const [line, setLine] = useState(null); // the job line being audited (its draft)
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);
  const [approving, setApproving] = useState(false);
  const { hasRole } = useAuth();
  // The Tariff Manager agrees a special rate for a job; Finance may also change it
  // while auditing the draft (8931), and approves the bill.
  const canSetRate = hasRole(ROLES.TARIFF) || hasRole(ROLES.FINANCE);
  const canApprove = hasRole(ROLES.FINANCE);
  const [rate, setRate] = useState('');
  const [rateReason, setRateReason] = useState('');
  const [rateSaving, setRateSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await api.listBookingInvoices();
    setInvoices(res || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load invoicing drafts.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const openLine = async (jobId) => {
    setRate('');
    setRateReason('');
    setLine(jobId ? await api.getInvoiceDraft(jobId) : null);
  };

  /** Re-read the invoice (and the line on show) after a change. */
  const refresh = async (bookingId, jobId) => {
    const inv = await api.getBookingInvoice(bookingId);
    setDetail(inv);
    if (jobId) setLine(await api.getInvoiceDraft(jobId));
    await load();
    return inv;
  };

  const review = async (invoice) => {
    setPageMsg(null);
    setDetail(null);
    setLine(null);
    try {
      const inv = await api.getBookingInvoice(invoice.bookingId);
      setDetail(inv);
      await openLine(inv.lines.find((l) => l.completed)?.jobId);
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to open the invoice.' });
    }
  };

  const approve = async () => {
    if (!detail) return;
    setApproving(true);
    setPageMsg(null);
    try {
      const res = await api.approveBookingInvoice(detail.bookingId);
      // Refresh the detail in place (do NOT call review() — it clears pageMsg).
      await refresh(detail.bookingId, line?.jobId);
      setPageMsg({
        type: 'success',
        text: `Invoice ${res.invoiceNo} sealed & approved — SAP billing enabled (${res.sapReference}).`,
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to approve.' });
    } finally {
      setApproving(false);
    }
  };

  const saveRate = async (remove) => {
    if (!line) return;
    setPageMsg(null);
    if (!remove && !(Number(rate) > 0)) {
      setPageMsg({ type: 'danger', text: 'Enter a special hourly rate above zero.' });
      return;
    }
    if (!remove && rateReason.trim().length < 3) {
      setPageMsg({ type: 'danger', text: 'Say why this job has a special rate.' });
      return;
    }
    setRateSaving(true);
    try {
      if (remove) await api.clearSpecialRate(line.jobId);
      else await api.setSpecialRate(line.jobId, { hourlyRate: Number(rate), reason: rateReason.trim() });
      await refresh(detail.bookingId, line.jobId);
      setRate('');
      setRateReason('');
      setPageMsg({
        type: 'success',
        text: remove
          ? `Special rate removed — job ${line.jobNo || ''} is back on the tariff.`
          : `Special rate applied to job ${line.jobNo || ''}.`,
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.details?.[0]?.message || err.message || 'Failed to save the special rate.' });
    } finally {
      setRateSaving(false);
    }
  };

  const b = line?.breakdown;
  const ack = line?.acknowledgements;
  const cur = detail?.currency;
  const drafts = invoices.filter((i) => i.status !== 'Approved');

  return (
    <div className="container-fluid py-4" data-testid="invoicing-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-file-invoice me-2" aria-hidden="true" />
                Invoicing Drafts &amp; Billing Approvals
              </h3>
              <p className="text-muted mb-0 small">
                One sales invoice per booking, with a line for every job and tug. Validate each job&apos;s Statement of
                Facts, then seal the invoice.
              </p>
            </div>
            <span className="badge bg-warning text-dark p-2">{drafts.length} Drafts</span>
          </div>
        </div>
      </div>

      {/* SaaS Software Provider Invoicing panel (UC 4fc8ee00 / b1ff9920).
          The provider-licensing billing stream is a separate, provider-only
          OPEX concern from these customer-facing draft bills. Its full
          workspace is the dedicated SaaS Provider Billing Desk, which only the
          System Administrator can open (8882), so only they see the link. */}
      {hasRole(ROLES.ADMIN) && (
      <div className="row mb-4">
        <div className="col-12">
          <div
            className="card border-0 shadow-sm border-start border-success"
            style={{ borderLeftWidth: '4px' }}
            data-testid="saas-provider-invoicing-panel"
          >
            <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-md-center">
              <div className="me-md-3">
                <h6 className="text-success fw-bold mb-1">
                  <i className="fas fa-file-invoice-dollar me-2" aria-hidden="true" />
                  SaaS Software Provider Invoicing
                </h6>
                <p className="text-muted small mb-0">
                  Software-provider transaction-licensing fees (provider OPEX) are billed separately
                  from these customer-facing draft bills and SAP accounts receivable. Review monthly
                  tugboat-use totals and dispatch the SaaS licensing invoice on the dedicated desk.
                </p>
              </div>
              <Link
                to="/saas-provider-billing-desk-invoicing"
                className="btn btn-outline-success fw-bold mt-3 mt-md-0 text-nowrap"
                data-testid="open-saas-billing-desk"
              >
                <i className="fas fa-arrow-right me-1" aria-hidden="true" />
                Open SaaS Provider Billing Desk
              </Link>
            </div>
          </div>
        </div>
      </div>
      )}

      {loadError && (
        <div className="alert alert-danger" data-testid="invoicing-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="invoice-message">
          {pageMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* LEFT: one invoice per booking */}
          <div className="col-xl-5 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-history me-2" aria-hidden="true" />
                  Customer Sales Invoices
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Invoice</th>
                        <th>Customer / Vessel</th>
                        <th>Total</th>
                        <th aria-label="action" />
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-4">
                            No billing drafts.
                          </td>
                        </tr>
                      )}
                      {invoices.map((d) => (
                        <tr
                          key={d.bookingId}
                          className={detail?.bookingId === d.bookingId ? 'table-primary' : ''}
                          data-testid="draft-row"
                          data-ref={d.bookingRef}
                          data-status={d.status}
                        >
                          <td>
                            <span className="fw-bold d-block">{d.bookingRef}</span>
                            <small className="text-muted d-block" data-testid="invoice-no">
                              {d.invoiceNo || 'No invoice yet'}
                            </small>
                            <span className={`badge ${STATUS_BADGE[d.status] || 'bg-light text-dark'}`} data-testid="invoice-status">
                              {d.status}
                            </span>
                          </td>
                          <td className="small">
                            {d.customerName || '—'}
                            <span className="d-block text-muted">{d.vesselName || '—'}</span>
                            <span className="d-block text-muted" data-testid="invoice-jobs">
                              {d.serviceName} · {d.completedJobs}/{d.jobCount} job{d.jobCount > 1 ? 's' : ''} done
                            </span>
                          </td>
                          <td className="fw-bold text-primary text-nowrap">{money(d.netAmount, d.currency)}</td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => review(d)}
                              data-testid="review-draft"
                            >
                              <i className="fas fa-search-dollar me-1" aria-hidden="true" />
                              Review
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: the consolidated invoice */}
          <div className="col-xl-7 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-search-dollar me-2 text-info" aria-hidden="true" />
                  Draft Bill Auditor
                </h5>
              </div>
              <div className="card-body">
                {!detail ? (
                  <div className="text-center text-muted py-5 my-4" data-testid="no-draft">
                    <i className="fas fa-receipt fa-3x mb-3 text-secondary" aria-hidden="true" />
                    <h6>No invoice selected</h6>
                    <p className="small">Click “Review” to audit an invoice, its job lines and their SoFs.</p>
                  </div>
                ) : (
                  <div data-testid="auditor">
                    {/* Sales invoice header (Harbor Star layout) */}
                    <div className="border rounded p-3 small mb-3" data-testid="invoice-header">
                      <div className="d-flex flex-wrap justify-content-between gap-2">
                        <div>
                          <div className="text-muted text-uppercase fw-bold">Sales Invoice</div>
                          <div className="fs-6 fw-bold" data-testid="invoice-number">
                            {detail.invoiceNo || '—'}
                          </div>
                        </div>
                        <span className={`badge align-self-start ${STATUS_BADGE[detail.status] || 'bg-light text-dark'}`} data-testid="invoice-detail-status">
                          {detail.status}
                        </span>
                      </div>
                      <div className="row mt-2">
                        <div className="col-sm-6">
                          <div><span className="text-muted">Customer:</span> <strong>{detail.customerName || '—'}</strong></div>
                          <div>
                            <span className="text-muted">Vessel:</span> <strong>{detail.vesselName || '—'}</strong>
                            {detail.vesselImo ? ` (IMO ${detail.vesselImo})` : ''}
                          </div>
                          <div><span className="text-muted">Booking:</span> <strong>{detail.bookingRef}</strong></div>
                        </div>
                        <div className="col-sm-6">
                          <div>
                            <span className="text-muted">Service:</span>{' '}
                            <strong data-testid="invoice-service">{detail.serviceName}</strong>
                          </div>
                          <div>
                            <span className="text-muted">Port / Area:</span>{' '}
                            {detail.portName || '—'}
                            {detail.assistanceAreaName ? ` — ${detail.assistanceAreaName}` : ''}
                          </div>
                          <div><span className="text-muted">Service date:</span> {fmt(detail.serviceDate)}</div>
                          <div data-testid="invoice-basis">
                            <span className="text-muted">Pricing:</span> {detail.pricing?.label || '—'}
                            {detail.lumpSum
                              ? ` — ${money(detail.lumpSum.amount, cur)} shared across ${detail.lumpSum.jobs} job(s)${
                                  detail.lumpSum.additionalTugs
                                    ? `, plus ${detail.lumpSum.additionalTugs} added tug(s)`
                                    : ''
                                }`
                              : ''}
                          </div>
                        </div>
                      </div>
                    </div>

                    {detail.status === 'In Progress' && (
                      <div className="alert alert-info py-2 small" data-testid="invoice-in-progress">
                        {detail.completedJobs} of {detail.jobCount} jobs are completed. The invoice can be approved once every
                        job&apos;s Statement of Facts is signed.
                      </div>
                    )}

                    {/* One line per job (tug) */}
                    <div className="table-responsive mb-3">
                      <table className="table table-sm align-middle small mb-0" data-testid="invoice-lines">
                        <thead className="bg-light text-secondary text-uppercase">
                          <tr>
                            <th>Job / Tug</th>
                            <th className="text-end">Hours</th>
                            <th className="text-end">Charge</th>
                            <th className="text-end">Standby</th>
                            <th className="text-end">Net</th>
                            <th aria-label="action" />
                          </tr>
                        </thead>
                        <tbody>
                          {detail.lines.map((l) => (
                            <tr
                              key={l.jobId}
                              className={line?.jobId === l.jobId ? 'table-active' : ''}
                              data-testid="invoice-line"
                              data-job-no={l.jobNo || ''}
                            >
                              <td>
                                <span className="fw-bold d-block">{l.jobNo || `Ticket ${l.ticketNo}`}</span>
                                {l.jobName}
                                <span className="d-block text-muted">
                                  {l.tugName || '—'} · Ticket No. {l.ticketNo ?? '—'}
                                </span>
                              </td>
                              {l.completed ? (
                                <>
                                  <td className="text-end">
                                    {l.breakdown.hours}
                                    {l.breakdown.billingNote ? (
                                      // 9034: "2h 10m actual → 2.5h billed (min 1h, 30-min increments)"
                                      <small className="d-block text-muted" data-testid="invoice-line-billing">
                                        {l.breakdown.billingNote}
                                      </small>
                                    ) : (
                                      l.breakdown.billedHours != null &&
                                      l.breakdown.billedHours !== l.breakdown.hours && (
                                        <small className="d-block text-muted">billed {l.breakdown.billedHours}</small>
                                      )
                                    )}
                                  </td>
                                  <td className="text-end">{money(l.breakdown.baseCharge, cur)}</td>
                                  <td className="text-end">{money(l.breakdown.standbyFees, cur)}</td>
                                  <td className="text-end fw-bold" data-testid="invoice-line-net">
                                    {money(l.breakdown.netAmount, cur)}
                                  </td>
                                  <td className="text-end">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => openLine(l.jobId)}
                                      data-testid="audit-line"
                                    >
                                      Audit
                                    </button>
                                  </td>
                                </>
                              ) : (
                                <td colSpan={5} className="text-muted">
                                  {l.status} — not yet completed
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {line && b && (
                      <>
                        {/* SoF inline preview for the job line */}
                        <div className="bg-light p-3 rounded small mb-3 border" data-testid="sof-preview">
                          <h6 className="fw-bold text-dark mb-1">
                            <i className="fas fa-ship me-1" aria-hidden="true" />
                            Statement of Facts Verification — {line.jobNo || `Ticket ${line.ticketNo}`}
                            {line.tugName ? ` · ${line.tugName}` : ''}
                          </h6>
                          <p className="mb-1 text-secondary">
                            Execution clocks:{' '}
                            <strong>{line.sof.clockEvents.map((c) => fmt(c.logged_at)).join(' → ') || '—'}</strong>
                          </p>
                          {line.sof.milestones.length > 0 && (
                            <ul className="mb-1 ps-3">
                              {line.sof.milestones.map((m) => (
                                <li key={m.id}>
                                  {MILESTONE_LABELS[m.milestone_type] || m.milestone_type}: {fmt(m.logged_at)}
                                </li>
                              ))}
                            </ul>
                          )}
                          {line.sof.delays.length > 0 && (
                            <p className="mb-1 text-secondary">Delays: {line.sof.delays.map((d) => d.reason).join(', ')}</p>
                          )}
                          {line.sof.signed ? (
                            <span className="badge bg-success mt-1" data-testid="sof-signed">
                              <i className="fas fa-signature me-1" aria-hidden="true" />
                              Captain E-signed ({line.sof.signedBy}) — SoF Locked
                            </span>
                          ) : (
                            <span className="badge bg-warning text-dark mt-1">SoF not yet signed</span>
                          )}
                          <div className="mt-2 pt-2 border-top" data-testid="acknowledgements">
                            <div data-testid="ack-tug-captain">
                              <i className={`fas ${ack.tugCaptain.acknowledged ? 'fa-check-circle text-success' : 'fa-circle-exclamation text-warning'} me-1`} aria-hidden="true" />
                              {ack.tugCaptain.acknowledged
                                ? `Job acknowledged${ack.tugCaptain.by ? ` by ${ack.tugCaptain.by}` : ''}${ack.tugCaptain.via === 'office' ? ' (recorded by the office)' : ''} — ${fmt(ack.tugCaptain.at)}`
                                : 'Job was not acknowledged by the tug captain'}
                            </div>
                            <div data-testid="ack-ship-master">
                              <i className={`fas ${ack.shipMaster.confirmed ? 'fa-check-circle text-success' : 'fa-circle-exclamation text-warning'} me-1`} aria-hidden="true" />
                              {ack.shipMaster.confirmed
                                ? `Assist confirmed by ${ack.shipMaster.by}${ack.shipMaster.via === 'office' ? ' (recorded by the office)' : ''} — ${fmt(ack.shipMaster.at)}`
                                : "Assist not yet confirmed by the ship's master"}
                            </div>
                          </div>
                        </div>

                        {/* The job's rate: the tariff's, or a special rate agreed for this job */}
                        <div className="border rounded p-3 small mb-3" data-testid="rate-panel">
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">
                              {b.pricing?.basis === 'LumpSum' && !b.specialRate ? 'Lump-sum share' : 'Hourly rate'}
                            </span>
                            <strong data-testid="hourly-rate">
                              {b.pricing?.basis === 'LumpSum' && !b.specialRate
                                ? money(b.baseCharge, b.currency)
                                : money(b.hourlyRate, b.currency)}
                              {b.specialRate
                                ? ' (special rate)'
                                : b.pricing?.source === 'ServiceRate'
                                  ? ` (${b.pricing.label.toLowerCase()})`
                                  : ' (rate card)'}
                            </strong>
                          </div>
                          {b.specialRate && (
                            <div className="text-muted mt-1" data-testid="special-rate-note">
                              {b.standardHourlyRate != null ? `Tariff: ${money(b.standardHourlyRate, b.currency)}. ` : ''}
                              Reason: {b.specialRate.reason} — {b.specialRate.setBy}, {fmt(b.specialRate.setAt)}
                            </div>
                          )}
                          {canSetRate && line.status !== 'Approved' && (
                            <div className="mt-2 pt-2 border-top">
                              <div className="row g-2">
                                <div className="col-4">
                                  <label htmlFor="special-rate" className="form-label small fw-bold text-muted mb-1">
                                    Special rate / hr
                                  </label>
                                  <input
                                    id="special-rate"
                                    type="number"
                                    min="0"
                                    step="any"
                                    className="form-control form-control-sm"
                                    value={rate}
                                    onChange={(e) => setRate(e.target.value)}
                                    data-testid="special-rate-input"
                                  />
                                </div>
                                <div className="col-8">
                                  <label htmlFor="special-rate-reason" className="form-label small fw-bold text-muted mb-1">
                                    Reason
                                  </label>
                                  <input
                                    id="special-rate-reason"
                                    className="form-control form-control-sm"
                                    value={rateReason}
                                    maxLength={1000}
                                    onChange={(e) => setRateReason(e.target.value)}
                                    data-testid="special-rate-reason"
                                  />
                                </div>
                              </div>
                              <div className="mt-2">
                                <button className="btn btn-sm btn-primary me-2" onClick={() => saveRate(false)} disabled={rateSaving} data-testid="apply-special-rate">
                                  Apply Special Rate
                                </button>
                                {b.specialRate && (
                                  <button className="btn btn-sm btn-outline-secondary" onClick={() => saveRate(true)} disabled={rateSaving} data-testid="remove-special-rate">
                                    Remove
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Invoice totals across every job */}
                    <ul className="list-group list-group-flush small mb-3" data-testid="invoice-totals">
                      <li className="list-group-item d-flex justify-content-between p-2">
                        <span className="text-muted">
                          {detail.lumpSum ? 'Service Charge (lump sum)' : `Base Towage Charge (${detail.totals.hours} hrs)`}:
                        </span>
                        <strong data-testid="breakdown-base">
                          {money(detail.totals.baseCharge - (detail.lumpSum?.additionalTugCharge || 0), cur)}
                        </strong>
                      </li>
                      {detail.lumpSum?.additionalTugs > 0 && (
                        <li className="list-group-item d-flex justify-content-between p-2" data-testid="breakdown-additional-tugs">
                          <span className="text-muted">Additional Tugs ({detail.lumpSum.additionalTugs}):</span>
                          <strong>{money(detail.lumpSum.additionalTugCharge, cur)}</strong>
                        </li>
                      )}
                      <li className="list-group-item d-flex justify-content-between p-2">
                        <span className="text-muted">Standby / Delay Fees ({detail.totals.standbyHours} hrs):</span>
                        <strong data-testid="breakdown-standby">{money(detail.totals.standbyFees, cur)}</strong>
                      </li>
                      <li className="list-group-item d-flex justify-content-between p-2">
                        <span className="text-muted">Surcharges{b ? ` (${b.surchargePercent}%)` : ''}:</span>
                        <strong data-testid="breakdown-surcharge">{money(detail.totals.surchargeAmount, cur)}</strong>
                      </li>
                      <li className="list-group-item d-flex justify-content-between p-2">
                        <span className="text-muted">VAT / Taxes{b ? ` (${b.vatPercent}%)` : ''}:</span>
                        <strong data-testid="breakdown-vat">{money(detail.totals.vatAmount, cur)}</strong>
                      </li>
                      <li className="list-group-item d-flex justify-content-between p-2 bg-light border-top">
                        <span className="fw-bold text-dark">Total Net Amount:</span>
                        <strong className="text-primary" data-testid="breakdown-net">
                          {money(detail.totals.netAmount, cur)}
                        </strong>
                      </li>
                    </ul>

                    {detail.status === 'Approved' ? (
                      <div className="alert alert-success mb-0" data-testid="draft-approved">
                        <i className="fas fa-lock me-1" aria-hidden="true" />
                        Invoice {detail.invoiceNo} sealed &amp; approved{line?.approvedBy ? ` by ${line.approvedBy}` : ''}. SAP
                        billing enabled — <strong data-testid="sap-reference">{line?.sapReference || '—'}</strong>.
                      </div>
                    ) : !canApprove ? (
                      <div className="alert alert-light border mb-0 small" data-testid="awaiting-finance">
                        Awaiting approval by Billing / Finance.
                      </div>
                    ) : (
                      <button
                        className="btn btn-success w-100"
                        onClick={approve}
                        disabled={approving || detail.status !== 'Draft'}
                        data-testid="approve-bill"
                      >
                        {approving && (
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        )}
                        <i className="fas fa-lock me-1" aria-hidden="true" />
                        Approve &amp; Seal Invoice
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
